import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Player, GolfCourse, Score, Tournament } from '../types';
import { Users, ChevronLeft, ChevronRight, Save, Flag, UserPlus, Edit2, CheckCircle, AlertCircle, X, Lock, Trophy, Heart, Wifi, WifiOff } from 'lucide-react';
import { SponsorDisplay } from './sponsors/SponsorDisplay';
import { useRegistration } from '../contexts/RegistrationContext';
import { ScoringLockWrapper } from './scoring/ScoringLockWrapper';
import { usePlayerLock } from '../contexts/PlayerLockContext';
import { verifyScoresWithServer, syncScores } from '../lib/scoring';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { useOfflineScoring } from '../hooks/useOfflineScoring';
import { ScoringAgent } from '../utils/ScoringAgent';

interface GroupScoringProps {
  players: Player[];
  course: GolfCourse;
  isAdmin: boolean;
  onTournamentUpdate: (tournament: Tournament) => void;
  selectedPlayers: Player[];
  setSelectedPlayers: (players: Player[]) => void;
  scores: Record<string, Record<number, number>>;
  setScores: (scores: Record<string, Record<number, number>>) => void;
  isSelectingPlayers: boolean;
  setIsSelectingPlayers: (isSelecting: boolean) => void;
  currentHoleIndex: number;
  setCurrentHoleIndex: (index: number) => void;
  onScoreSubmit: (scores: Record<string, Score[]>) => void;
  onResetScoring: () => void;
  completedHoles: number[];
  setCompletedHoles: (holes: number[]) => void;
  tournament: Tournament;
  selectedTee?: {
    slope: number;
  };
  startRegistration: () => void;
}

interface RetryQueueItem {
  scores: Record<string, Score[]>;
  attempts: number;
  version: number;
  timestamp: number;
}

interface Discrepancy {
    playerId: string;
    hole: number;
    localScore: number;
  serverScore: number;
}

interface StoredScore {
  playerId: string;
  hole: number;
  strokes: number;
  timestamp: number;
  version: number;
  synced: boolean;
}

interface ScoreCache {
  tournamentId: string;
  scores: Record<string, StoredScore[]>;
  lastSyncTimestamp: number;
  version: number;
}

interface SyncState {
  isOnline: boolean;
  lastSyncAttempt: number;
  syncErrors: Record<string, string>;
}

// Add this function before the GroupScoring component
const mergeScores = (
  existingScores: Score[],
  newScores: Score[],
  playerId: string,
  tournamentId: string
): Score[] => {
  // Create a map of existing scores by hole number
  const scoreMap = new Map<number, Score>();
  existingScores.forEach(score => {
    scoreMap.set(score.hole, score);
  });

  // Merge new scores with existing ones
  newScores.forEach(newScore => {
    const existingScore = scoreMap.get(newScore.hole);
    if (!existingScore || newScore.timestamp > existingScore.timestamp) {
      scoreMap.set(newScore.hole, {
        ...newScore,
        playerId,
        tournamentId,
        timestamp: newScore.timestamp || Date.now()
      });
    }
  });

  // Convert map back to array and sort by hole number
  return Array.from(scoreMap.values()).sort((a, b) => a.hole - b.hole);
};

function GroupScoring({ 
  players,
  course,
  isAdmin,
  selectedPlayers,
  onTournamentUpdate,
  setSelectedPlayers,
  scores,
  setScores,
  isSelectingPlayers,
  setIsSelectingPlayers,
  currentHoleIndex,
  setCurrentHoleIndex,
  onScoreSubmit,
  onResetScoring,
  completedHoles,
  setCompletedHoles,
  tournament,
  selectedTee,
  startRegistration
}: GroupScoringProps) {
  const [startingHole, setStartingHole] = useState(1);
  const [currentScores, setCurrentScores] = useState<Record<string, number>>({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showTooManyPlayersError, setShowTooManyPlayersError] = useState(false);
  const MAX_PLAYERS = 5;
  const { unlockPlayers, lockPlayers } = usePlayerLock();
  const [hasEnteredScores, setHasEnteredScores] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [retryQueue, setRetryQueue] = useState<RetryQueueItem[]>(() => {
    // Initialize from localStorage if available
    const savedQueue = localStorage.getItem('hvga_retry_queue');
    return savedQueue ? JSON.parse(savedQueue) : [];
  });
  const MAX_RETRIES = 3;
  const scoreVersionRef = useRef(Date.now());
  const [syncStatus, setSyncStatus] = useState<Record<string, 'pending' | 'synced' | 'failed'>>({});
  const [lastSyncedScores, setLastSyncedScores] = useState<Record<string, Score[]>>({});
  const [syncState, setSyncState] = useState<SyncState>({
    isOnline: navigator.onLine,
    lastSyncAttempt: 0,
    syncErrors: {}
  });
  const [offlineScores, setOfflineScores] = useState<Record<string, StoredScore[]>>({});
  const [localScores, setLocalScores] = useState<Score[]>([]);
  const scoringAgent = useMemo(() => ScoringAgent.getInstance(), []);

  // Add our new offline scoring hook
  const { 
    isOnline, 
    isSyncing, 
    syncError, 
    saveScore: saveOfflineScore, 
    sync: syncOfflineScores 
  } = useOfflineScoring(tournament.id);

  // Load cached scores from localStorage on component mount
  useEffect(() => {
    const cachedData = localStorage.getItem(`hvga_scores_${tournament.id}`);
    if (cachedData) {
      try {
        const parsed: ScoreCache = JSON.parse(cachedData);
        if (parsed.tournamentId === tournament.id) {
          setOfflineScores(parsed.scores);
          scoreVersionRef.current = parsed.version;
        }
      } catch (e) {
        console.error('Failed to parse cached scores:', e);
      }
    }
  }, [tournament.id]);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      setSyncError(null);
      
      if (tournament?.id) {
        try {
          setIsSyncing(true);
          await handleSyncOfflineScores();
          toast.success('Successfully synced offline scores');
        } catch (error) {
          console.error('Failed to sync offline scores:', error);
          setSyncError('Failed to sync offline scores. Will retry automatically.');
          toast.error('Failed to sync offline scores. Will retry automatically.');
        } finally {
          setIsSyncing(false);
        }
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.info('You are offline. Scores will be saved locally and synced when connection is restored.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle registration deadline check
  const isRegistrationClosed = React.useMemo(() => {
    if (!tournament) return true;
    
    const now = new Date();
    const deadline = tournament.registrationDeadline ? 
      (() => {
        const d = new Date(tournament.registrationDeadline);
        d.setUTCHours(23, 59, 59, 999);
        return d;
      })() :
      new Date(tournament.startDate);
    
    return now > deadline;
  }, [tournament?.registrationDeadline, tournament?.startDate]);

  // Ensure tournament is defined
  if (!tournament) {
    return null;
  }

  // Find next uncompleted hole for players with existing scores
  useEffect(() => {
    if (selectedPlayers.length === 0) return;
    
    // Only update if any player has scores
    if (selectedPlayers.some(p => p.scores && p.scores.length > 0)) {
      const completedHoles = new Set<number>();
      selectedPlayers.forEach(player => {
        if (player.scores) {
          player.scores.forEach(score => {
            completedHoles.add(score.hole);
          });
        }
      });

      // Find first uncompleted hole
      for (let i = 1; i <= 18; i++) {
        if (!completedHoles.has(i)) {
          setStartingHole(i);
          break;
        }
      }
    }
  }, [selectedPlayers]);

  // Add confirmation dialog for back navigation
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);

  // Check if all 18 holes have scores for all players
  const areAllHolesCompleted = () => {
    for (let hole = 1; hole <= course.parValues.length; hole++) {
      if (!validateHoleScores(hole)) {
          return false;
      }
    }
    return true;
  };

  // Filter players for current tournament and sort alphabetically
  const tournamentPlayers = players.filter(p => p.tournamentId === tournament?.id);
  const sortedPlayers = [...tournamentPlayers].sort((a, b) => a.name.localeCompare(b.name));

  // Check if any selected players have existing scores
  const hasExistingScores = selectedPlayers.some(p => p.scores && p.scores.length > 0);

  // Check if a player has completed their round
  const hasCompletedRound = (player: Player) => {
    if (!player.scores) return false;
    const completedHoles = new Set(player.scores.map(s => s.hole));
    return completedHoles.size === 18;
  };

  const handleStartScoring = async () => {
    if (selectedPlayers.length === 0) return;
    
    // Prevent scoring if tournament is completed
    if (tournament.status === 'completed') {
      alert('Tournament is completed. Scoring is not allowed.');
      return;
    }

    // Check if any selected player has already completed their round
    const hasCompletedPlayers = selectedPlayers.some(player => {
      const playerScores = scores[player.id] || {};
      return Object.keys(playerScores).length === 18;
    });

    if (hasCompletedPlayers) {
      alert('One or more selected players have already completed their round. Please select different players.');
      return;
    }
    
    // Find the next uncompleted hole for players with existing scores
    let nextUncompleted = startingHole;
    if (selectedPlayers.some(p => p.scores && p.scores.length > 0)) {
      const completedHoles = new Set<number>();
      selectedPlayers.forEach(player => {
        if (player.scores) {
          player.scores.forEach(score => {
            completedHoles.add(score.hole);
          });
        }
      });

      // Find first uncompleted hole
      for (let i = 1; i <= 18; i++) {
        if (!completedHoles.has(i)) {
          nextUncompleted = i;
          break;
        }
      }
    }

    // Initialize scores with existing scores or par values for each player
    const initialScores: Record<string, Record<number, number>> = {};
    const initialCurrentScores: Record<string, number> = {};
    
    // First try to restore from localStorage
    const localStorageKey = `hvga_scores_${tournament.id}`;
    const storedState = localStorage.getItem(localStorageKey);
    if (storedState) {
      try {
        const parsedState = JSON.parse(storedState);
        if (parsedState.tournamentId === tournament.id) {
          // Restore scores from localStorage
          Object.entries(parsedState.scores || {}).forEach(([playerId, playerScores]) => {
            if (selectedPlayers.some(p => p.id === playerId)) {
              initialScores[playerId] = playerScores as Record<number, number>;
            }
          });
        }
      } catch (error) {
        console.error('Failed to parse stored scores:', error);
      }
    }
    
    // Then update with any server scores that might be newer
    selectedPlayers.forEach(player => {
      if (player.scores && player.scores.length > 0) {
        // Convert array of scores to record format
        const serverScores: Record<number, number> = {};
        player.scores.forEach(score => {
          serverScores[score.hole] = score.strokes;
        });
        
        // Merge with localStorage scores, preferring server scores if they exist
        initialScores[player.id] = {
          ...initialScores[player.id],
          ...serverScores
        };
      }
    });
    
    // Set current hole scores
    selectedPlayers.forEach(player => {
      const holeScore = initialScores[player.id]?.[nextUncompleted];
      initialCurrentScores[player.id] = holeScore || course.parValues[nextUncompleted - 1];
    });
    
    // Try to acquire locks for all selected players
    const success = await lockPlayers(selectedPlayers.map(p => p.id), tournament.id);
    if (success) {
      // Only proceed if we got the locks
      setCurrentHoleIndex(nextUncompleted - 1);
      setIsSelectingPlayers(false);
      setScores(initialScores);
      setCurrentScores(initialCurrentScores);
    } else {
      // If lock acquisition fails, show error and return
      alert('Some players are currently being scored by another user. Please try again.');
    }
  };

  // Replace the existing syncOfflineScores function
  const handleSyncOfflineScores = async () => {
    if (!tournament?.id) return;
    
    try {
      const result = await syncOfflineScores();
      
      if (result.success) {
        // Update UI to reflect successful sync
        toast.success(`Successfully synced ${result.syncedCount} scores`);
      } else if (result.errors.length > 0) {
        // Show errors but don't block the user
        toast.error(`Sync issues: ${result.errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Failed to sync scores:', error);
      toast.error('Failed to sync scores with server');
    }
  };

  // Add recovery of stored state on component mount
  useEffect(() => {
    if (!tournament?.id) return;

    const localStorageKey = `hvga_scores_${tournament.id}`;
    try {
      const storedState = localStorage.getItem(localStorageKey);
      if (storedState) {
        const parsedState = JSON.parse(storedState);
        
        // Only restore if it's for the current tournament
        if (parsedState.tournamentId === tournament.id) {
          setScores(parsedState.scores || {});
          setOfflineScores(parsedState.offlineScores || {});
          setCurrentHoleIndex(parsedState.currentHoleIndex || 0);
          setCompletedHoles(parsedState.completedHoles || []);
          
          // Initialize current scores for the current hole
          const currentHoleScores: Record<string, number> = {};
          selectedPlayers.forEach(player => {
            const holeScore = parsedState.scores?.[player.id]?.[parsedState.currentHoleIndex + 1];
            if (holeScore) {
              currentHoleScores[player.id] = holeScore;
            } else {
              currentHoleScores[player.id] = course.parValues[parsedState.currentHoleIndex];
            }
          });
          setCurrentScores(currentHoleScores);
        }
      }
    } catch (error) {
      console.error('Failed to restore from localStorage:', error);
    }
  }, [tournament?.id, selectedPlayers, course.parValues]);

  // Initialize scoring agent when tournament changes
  useEffect(() => {
    if (tournament?.id) {
      scoringAgent.initialize(tournament.id);
    }
  }, [tournament?.id, scoringAgent]);

  const handleScoreChange = (playerId: string, value: string) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue <= 0) return;

    // Update current scores for immediate UI feedback
    setCurrentScores(prev => ({
      ...prev,
      [playerId]: numValue
    }));

    // Update the main scores object
    setScores(prev => {
      const newScores = { ...prev };
      if (!newScores[playerId]) {
        newScores[playerId] = {};
      }
      newScores[playerId] = {
        ...newScores[playerId],
        [currentHoleIndex + 1]: numValue
      };
      return newScores;
    });

    // Mark that we have entered scores
    setHasEnteredScores(true);
  };

  const handleSubmitScores = async () => {
    if (!tournament?.id) return;
    
    try {
      setIsSubmitting(true);

      // Format scores for submission
      const formattedScores: Score[] = [];
      selectedPlayers.forEach(player => {
        const holeHandicap = selectedTee && player.handicap
          ? Math.round((player.handicap * selectedTee.slope) / (113 * 18))
          : 0;

        // Get the current hole's score
        const currentHoleScore = currentScores[player.id] || course.parValues[currentHoleIndex];
        
        // Add current hole score to player's scores if not already present
        if (!scores[player.id]) {
          scores[player.id] = {};
        }
        if (!scores[player.id][currentHoleIndex + 1]) {
          scores[player.id][currentHoleIndex + 1] = currentHoleScore;
        }

        // Format score for submission
        formattedScores.push({
          playerId: player.id,
          tournamentId: tournament.id,
          hole: currentHoleIndex + 1,
          strokes: currentHoleScore,
          timestamp: Date.now()
        });
      });

      // Submit scores through scoring agent
      await scoringAgent.submitScores(tournament.id, formattedScores);

      // Update completed holes
      const newCompletedHoles = [...completedHoles, currentHoleIndex + 1];
      setCompletedHoles(newCompletedHoles);

      // Move to next hole
      let nextHole = currentHoleIndex + 1;
      if (nextHole >= course.parValues.length) {
        nextHole = 0;
      }
      setCurrentHoleIndex(nextHole);

      // Reset current scores for next hole with par values
      const nextHoleScores: Record<string, number> = {};
      selectedPlayers.forEach(player => {
        nextHoleScores[player.id] = course.parValues[nextHole];
      });
      setCurrentScores(nextHoleScores);

    } catch (error) {
      console.error('Failed to submit scores:', error);
      toast.error('Failed to submit scores. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmComplete = async () => {
    if (!tournament?.id) return;
    
    try {
      setIsSubmitting(true);

      // Format all scores for submission
      const formattedScores: Score[] = [];
      selectedPlayers.forEach(player => {
        Object.entries(scores[player.id] || {}).forEach(([hole, strokes]) => {
          formattedScores.push({
            playerId: player.id,
            tournamentId: tournament.id,
            hole: parseInt(hole),
            strokes,
            timestamp: Date.now()
          });
        });
      });

      // Submit final scores through scoring agent
      await scoringAgent.submitScores(tournament.id, formattedScores);

      // Update player status in database for each player
      for (const player of selectedPlayers) {
        const playerScores = formattedScores.filter(s => s.playerId === player.id);
        const totalScore = playerScores.reduce((sum, score) => sum + score.strokes, 0);
        
        const { error: updateError } = await supabase
          .from('players')
          .update({ 
            round_completed: true,
            scores: playerScores,
            total_score: totalScore
          })
          .eq('id', player.id)
          .eq('tournament_id', tournament.id);

        if (updateError) throw updateError;
      }

      // Reset scoring state
      setScores({});
      setCompletedHoles([]);
      setCurrentHoleIndex(0);
      setCurrentScores({});
      setShowConfirmation(false);
      setSelectedPlayers([]);
      setIsSelectingPlayers(true);

      // Show success message
      toast.success('Round completed successfully');

      // Redirect to leaderboard
      if (window.location.pathname.includes('/scoring')) {
        window.location.href = window.location.pathname.replace('/scoring', '/leaderboard');
      }

    } catch (error) {
      console.error('Failed to complete round:', error);
      toast.error('Failed to complete round. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add validation helper function
  const validateHoleScores = (holeNumber: number): boolean => {
    if (!selectedPlayers.length) return false;
    
    for (const player of selectedPlayers) {
      const playerScores = scores[player.id];
      if (!playerScores || !playerScores[holeNumber]) {
        return false;
      }
      const score = playerScores[holeNumber];
      if (typeof score !== 'number' || score <= 0) {
        return false;
      }
    }
    return true;
  };

  // Save retry queue to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('hvga_retry_queue', JSON.stringify(retryQueue));
  }, [retryQueue]);

  // Update processRetryQueue to use the updated merge function
  useEffect(() => {
    const processRetryQueue = async () => {
      if (!navigator.onLine || retryQueue.length === 0 || !tournament?.id) return;
      
      // Sort by version and timestamp to maintain order
      const sortedQueue = [...retryQueue].sort((a, b) => {
        if (a.version !== b.version) return a.version - b.version;
        return a.timestamp - b.timestamp;
      });
      
      // Clear queue immediately to prevent duplicate processing
      setRetryQueue([]);
      
      for (const item of sortedQueue) {
        try {
          if (item.attempts >= MAX_RETRIES) continue;
          
          // Process each player's scores
          for (const [playerId, playerScores] of Object.entries(item.scores)) {
            // Get existing scores
            const { data: existingData, error: fetchError } = await supabase
              .from('players')
              .select('scores')
              .eq('id', playerId)
              .eq('tournament_id', tournament.id)
              .single();

            if (fetchError && fetchError.code !== 'PGRST116') {
              throw fetchError;
            }

            // Merge scores
            const existingScores = existingData?.scores || [];
            const mergedScores = mergeScores(existingScores, playerScores, playerId, tournament.id);
            const totalScore = mergedScores.reduce((sum, score) => sum + score.strokes, 0);

            // Update with merged scores
            const { error: updateError } = await supabase
              .from('players')
              .update({ 
                scores: mergedScores,
                total_score: totalScore,
                updated_at: new Date().toISOString()
              })
              .eq('id', playerId)
              .eq('tournament_id', tournament.id);

            if (updateError) throw updateError;
          }
          
          toast.success('Offline scores synced successfully');
    } catch (error) {
          console.error('Retry failed:', error);
          
          const updatedItem: RetryQueueItem = {
            ...item,
            attempts: item.attempts + 1,
            timestamp: Date.now()
          };
          
          if (updatedItem.attempts < MAX_RETRIES) {
            setRetryQueue(prev => [...prev, updatedItem]);
          }
        }
      }
    };

    const retryInterval = setInterval(processRetryQueue, 5000);
    window.addEventListener('online', processRetryQueue);
    
    return () => {
      clearInterval(retryInterval);
      window.removeEventListener('online', processRetryQueue);
    };
  }, [retryQueue, tournament?.id]);

  // Add new state for editing mode
  const [editingHole, setEditingHole] = useState<number | null>(null);

  // Modify handleEditHole to be simpler without score history table
  const handleEditHole = (holeIndex: number) => {
    if (tournament.status === 'completed') {
      alert('Tournament is completed. Score editing is not allowed.');
      return;
    }

    setEditingHole(holeIndex);
    setCurrentHoleIndex(holeIndex);
    
    // Pre-fill current scores for editing
    const editScores: Record<string, number> = {};
    selectedPlayers.forEach(player => {
      editScores[player.id] = scores[player.id]?.[holeIndex + 1] || course.parValues[holeIndex];
    });
    setCurrentScores(editScores);
  };

  // Add function to exit editing mode
  const exitEditingMode = () => {
    setEditingHole(null);
    
    // Find next uncompleted hole
    let nextHoleIndex = currentHoleIndex;
    let foundNextHole = false;
    
    // First try holes after the current one
    for (let i = currentHoleIndex + 1; i < 18; i++) {
      if (!completedHoles.includes(i + 1)) {
        nextHoleIndex = i;
        foundNextHole = true;
        break;
      }
    }
    
    // If no uncompleted holes found after current, start from beginning
    if (!foundNextHole) {
      for (let i = 0; i < currentHoleIndex; i++) {
        if (!completedHoles.includes(i + 1)) {
          nextHoleIndex = i;
          foundNextHole = true;
          break;
        }
      }
    }
    
    const nextHoleScores: Record<string, number> = {};
    selectedPlayers.forEach(player => {
      nextHoleScores[player.id] = course.parValues[nextHoleIndex];
    });
    
    setCurrentHoleIndex(nextHoleIndex);
    setCurrentScores(nextHoleScores);
  };

  // Add helper function for score formatting
  const getScoreClass = (score: number, par: number) => {
    const diff = score - par;
    if (diff <= -2) return 'text-purple-600 font-medium'; // Eagle or better
    if (diff === -1) return 'text-green-600 font-medium'; // Birdie
    if (diff === 0) return 'text-gray-900 font-medium';   // Par
    if (diff === 1) return 'text-orange-600 font-medium'; // Bogey
    return 'text-red-600 font-medium';                    // Double bogey or worse
  };

  const getHoleStatus = (holeNumber: number): 'current' | 'completed' | 'pending' => {
    if (holeNumber === currentHoleIndex + 1) {
      return 'current';
    }
    if (completedHoles.includes(holeNumber)) {
      return 'completed';
    }
    return 'pending';
  };

  // Add verification effect
  useEffect(() => {
    if (!tournament || isSelectingPlayers) return;

    const verifyScores = async () => {
      if (!scores || Object.keys(scores).length === 0) return;

      // Convert scores object to array format
      const formattedScores: Record<string, Score[]> = {};
      Object.entries(scores).forEach(([playerId, playerScores]) => {
        const scoreArray = Object.entries(playerScores).map(([hole, strokes]) => {
          const holeHandicap = selectedTee && players.find(p => p.id === playerId)?.handicap
            ? Math.round((players.find(p => p.id === playerId)!.handicap! * selectedTee.slope) / (113 * 18))
            : 0;

          return {
            hole: parseInt(hole, 10),
            strokes,
            netStrokes: strokes - holeHandicap,
            tournamentId: tournament.id,
            playerId: playerId,
            synced: false
          };
        });
        formattedScores[playerId] = scoreArray;
      });

      try {
        const result = await verifyScoresWithServer(tournament.id, formattedScores);
        
        if (!result.isMatch) {
          // Found discrepancies, update sync status
          result.discrepancies.forEach((discrepancy: Discrepancy) => {
            setSyncStatus(prev => ({
              ...prev,
              [discrepancy.playerId]: 'failed'
            }));
          });

          // Add to retry queue if not already there
          const retryItem: RetryQueueItem = {
            scores: formattedScores,
            attempts: 0,
            version: scoreVersionRef.current,
            timestamp: Date.now()
          };
          
          // Only add scores that have discrepancies
          const affectedPlayerIds = new Set(result.discrepancies.map((d: Discrepancy) => d.playerId));
          const retryScores: Record<string, Score[]> = {};
          affectedPlayerIds.forEach((playerId: string) => {
            if (formattedScores[playerId]) {
              retryScores[playerId] = formattedScores[playerId];
            }
          });

          if (Object.keys(retryScores).length > 0) {
            setRetryQueue(prev => {
              // Check if these scores are already in the queue
              const isAlreadyQueued = prev.some(item => 
                Object.keys(item.scores).some(pid => 
                  retryScores[pid]?.some(score => 
                    item.scores[pid]?.some(s => 
                      s.hole === score.hole && s.strokes === score.strokes
                    )
                  )
                )
              );

              if (!isAlreadyQueued) {
                return [...prev, { ...retryItem, scores: retryScores }];
              }
              return prev;
            });
          }
        }
      } catch (error) {
        console.error('Score verification failed:', error);
      }
    };

    const verificationInterval = setInterval(verifyScores, 30000);
    verifyScores(); // Initial verification

    return () => clearInterval(verificationInterval);
  }, [tournament, scores, isSelectingPlayers, players, selectedTee]);

  const updateScore = async (playerId: string, holeNumber: number, strokes: number) => {
    try {
      const updatedScores = [...localScores];
      const scoreIndex = updatedScores.findIndex(s => s.hole === holeNumber);
      
      if (scoreIndex >= 0) {
        updatedScores[scoreIndex] = { ...updatedScores[scoreIndex], strokes };
      } else {
        updatedScores.push({ hole: holeNumber, strokes });
      }

      // Update local state immediately for responsiveness
      setLocalScores(updatedScores);

      // Sync with server
      await syncScores(playerId, tournament.id, updatedScores);
      
      // Update player's total score
      const totalScore = updatedScores.reduce((sum, score) => sum + (score.strokes || 0), 0);
      const { error: updateError } = await supabase
        .from('players')
        .update({ total_score: totalScore })
        .eq('id', playerId)
        .eq('tournament_id', tournament.id);

      if (updateError) throw updateError;

      // Show success message
      toast.success('Score updated successfully');
    } catch (error) {
      console.error('Error updating score:', error);
      toast.error('Failed to update score. Please try again.');
      // Revert local state on error
      setLocalScores(prevScores => [...prevScores]);
    }
  };

  // Add a sync status indicator in the UI
  const renderSyncStatus = () => {
    if (isSyncing) {
      return (
        <div className="flex items-center text-amber-600">
          <span className="animate-spin mr-2">⟳</span>
          Syncing scores...
        </div>
      );
    }
    
    if (syncError) {
      return (
        <div className="flex items-center text-red-600">
          <span className="mr-2">⚠</span>
          Sync error: {syncError}
        </div>
      );
    }
    
    if (!isOnline) {
      return (
        <div className="flex items-center text-amber-600">
          <span className="mr-2">📶</span>
          Offline (Scores will sync when connection is restored)
        </div>
      );
    }
    
    return (
      <div className="flex items-center text-green-600">
        <span className="mr-2">✓</span>
        Online
      </div>
    );
  };

  // Update the completed holes display logic
  const getCompletedHolesCount = () => {
    const completedCount = selectedPlayers.reduce((count, player) => {
      const playerScores = scores[player.id] || {};
      return Math.max(count, Object.keys(playerScores).length);
    }, 0);
    return completedCount;
  };

  // Add effect to check for completed players on mount and after refresh
  useEffect(() => {
    if (selectedPlayers.length > 0) {
      const allPlayersCompleted = selectedPlayers.every(player => hasCompletedRound(player));
      if (allPlayersCompleted) {
        // Reset all scoring states
        setScores({});
        setCompletedHoles([]);
        setCurrentHoleIndex(0);
        setCurrentScores({});
        setShowConfirmation(false);
        setSelectedPlayers([]);
        setIsSelectingPlayers(true);
        
        // Release any existing locks
        unlockPlayers(selectedPlayers.map(p => p.id), tournament.id);
        
        // Redirect to leaderboard
        if (window.location.pathname.includes('/scoring')) {
          window.location.href = window.location.pathname.replace('/scoring', '/leaderboard');
        }
      }
    }
  }, [selectedPlayers, tournament?.id]);

  // Add effect to prevent scoring mode for completed tournament
  useEffect(() => {
    if (tournament?.status === 'completed' && !isSelectingPlayers) {
      setIsSelectingPlayers(true);
      setSelectedPlayers([]);
      // Redirect to leaderboard
      if (window.location.pathname.includes('/scoring')) {
        window.location.href = window.location.pathname.replace('/scoring', '/leaderboard');
      }
    }
  }, [tournament?.status, isSelectingPlayers]);

  const resetPlayerScores = async (playerId: string) => {
    try {
      // Reset scores through scoring agent
      await scoringAgent.resetScores(tournament.id, playerId);

      // Update local state
      setScores(prev => {
        const newScores = { ...prev };
        delete newScores[playerId];
        return newScores;
      });

      // Reset current scores
      setCurrentScores(prev => {
        const newCurrentScores = { ...prev };
        delete newCurrentScores[playerId];
        return newCurrentScores;
      });

      // Update completed holes
      setCompletedHoles(prev => {
        const newCompletedHoles = [...prev];
        return newCompletedHoles.filter(hole => {
          const playerScores = scores[playerId] || {};
          return !playerScores[hole];
        });
      });

      toast.success('Scores reset successfully');
    } catch (error) {
      console.error('Failed to reset scores:', error);
      toast.error('Failed to reset scores. Please try again.');
    }
  };

  // Add navigation functions
  const navigateToPreviousHole = () => {
    if (currentHoleIndex > 0) {
      setCurrentHoleIndex(currentHoleIndex - 1);
      // Pre-fill scores for the previous hole
      const prevHoleScores: Record<string, number> = {};
      selectedPlayers.forEach(player => {
        prevHoleScores[player.id] = scores[player.id]?.[currentHoleIndex] || course.parValues[currentHoleIndex - 1];
      });
      setCurrentScores(prevHoleScores);
    }
  };

  const navigateToNextHole = () => {
    if (currentHoleIndex < course.parValues.length - 1) {
      setCurrentHoleIndex(currentHoleIndex + 1);
      // Pre-fill scores for the next hole
      const nextHoleScores: Record<string, number> = {};
      selectedPlayers.forEach(player => {
        nextHoleScores[player.id] = scores[player.id]?.[currentHoleIndex + 2] || course.parValues[currentHoleIndex + 1];
      });
      setCurrentScores(nextHoleScores);
    }
  };

  // Add new state for review modal
  const [showReviewModal, setShowReviewModal] = useState(false);

  // Add function to handle score review
  const handleScoreReview = () => {
    setShowConfirmation(false);
    setShowReviewModal(true);
  };

  // Add function to handle review completion
  const handleReviewComplete = async () => {
    setShowReviewModal(false);
    await handleConfirmComplete();
  };

  // Modify the main render to use new components
  if (isSelectingPlayers) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        {tournament.status === 'completed' ? (
          <div className="text-center py-12">
            <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Tournament Completed
              </h3>
              <p className="text-gray-600 max-w-sm mx-auto">
                This tournament has been completed. No further scoring is allowed.
              </p>
            </div>
          </div>
        ) : tournament.status !== 'active' ? (
          <div className="text-center py-12">
            <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Tournament Not Active
              </h3>
              <p className="text-gray-600 max-w-sm mx-auto">
                Please wait for an administrator to activate the tournament before entering scores.
              </p>
            </div>
          </div>
        ) : tournamentPlayers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Players Available
              </h3>
              <p className="text-gray-600 max-w-sm mx-auto">
                No players have been added to this tournament yet. Please add players in the Setup tab.
              </p>
            </div>
          </div>
        ) : (
          <ScoringLockWrapper
            players={sortedPlayers}
            onPlayerSelect={(player) => {
              const isSelected = selectedPlayers.some(p => p.id === player.id);
              if (isSelected) {
                setSelectedPlayers((prev: Player[]) => prev.filter(p => p.id !== player.id));
              } else {
                if (selectedPlayers.length >= MAX_PLAYERS) {
                  setShowTooManyPlayersError(true);
                  return;
                }
                setSelectedPlayers((prev: Player[]) => [...prev, player]);
              }
            }}
            selectedPlayers={selectedPlayers}
            maxPlayers={MAX_PLAYERS}
          >
            {selectedPlayers.length > 0 && (
              <>
                {!hasExistingScores && (
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1">
                      <label htmlFor="startingHole" className="block text-sm font-medium text-gray-700 mb-1">
                        Starting Hole
                      </label>
                      <select
                        id="startingHole"
                        name="startingHole"
                        value={startingHole}
                        onChange={(e) => setStartingHole(parseInt(e.target.value, 10))}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                      >
                        {[...Array(18)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>
                            Hole {i + 1} (Par {course.parValues[i]})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <button
                  onClick={handleStartScoring}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center"
                >
                  <Flag className="w-5 h-5 mr-2" />
                  {hasExistingScores 
                    ? `Continue Scoring (${selectedPlayers.length} Players)`
                    : `Start Scoring (${selectedPlayers.length} Players)`
                  }
                </button>
              </>
            )}
          </ScoringLockWrapper>
        )}
      </div>
    );
  }

  // Modify the score entry section to be the main interface
  const renderScoreEntry = () => (
    <div className="flex-1 space-y-4">
      <div className="flex flex-col mb-4">
        <div className="text-xs text-gray-500 mb-2">
          {getCompletedHolesCount()} of 18 holes completed
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={navigateToPreviousHole}
              disabled={currentHoleIndex === 0}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed mr-2"
            >
              -
            </button>
            <div className="mx-2">
              <h2 className="text-xl font-semibold">
                {editingHole !== null ? `Editing Hole ${editingHole + 1}` : `Hole ${currentHoleIndex + 1}`}
              </h2>
              <div className="text-sm text-gray-600">
                Par {course.parValues[editingHole !== null ? editingHole : currentHoleIndex]}
              </div>
            </div>
            <button
              onClick={navigateToNextHole}
              disabled={currentHoleIndex === course.parValues.length - 1}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed ml-2"
            >
              +
            </button>
          </div>
          {editingHole !== null ? (
            <button
              onClick={exitEditingMode}
              className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
            >
              Exit Editing
            </button>
          ) : (
            <button
              onClick={() => {
                unlockPlayers(selectedPlayers.map(p => p.id), tournament.id);
                setIsSelectingPlayers(true);
              }}
              className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
            >
              Back to<br />Players List
            </button>
          )}
        </div>
      </div>
      
      {/* Network Status Indicator */}
      <div className="flex items-center justify-end gap-2 text-sm">
        {renderSyncStatus()}
      </div>

      {Object.entries(syncState.syncErrors).length > 0 && (
        <div className="bg-red-50 text-red-600 p-2 rounded-md text-sm">
          Failed to sync some scores. They will be retried automatically.
        </div>
      )}

      {selectedPlayers.map(player => (
        <div key={player.id} className="flex items-center justify-between">
          <div className="flex-1">
            <div className="font-medium">
              {player.name}
              {offlineScores[player.id]?.some(s => !s.synced) && (
                <span className="ml-2 text-amber-500 text-sm">(Pending Sync)</span>
              )}
            </div>
            <div className="text-sm text-gray-600">
              {player.handicap ? `HC: ${player.handicap}` : 'SCR'}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                const currentScore = currentScores[player.id] || course.parValues[currentHoleIndex];
                if (currentScore > 1) {
                  handleScoreChange(player.id, (currentScore - 1).toString());
                }
              }}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
              type="button"
            >
              -
            </button>
            <div className="w-14 text-center">
              {currentScores[player.id] ? (
                <div>
                  <div className="font-medium">
                    {currentScores[player.id]}
                  </div>
                  <div className="text-xs text-gray-500">
                    {(() => {
                      const relation = currentScores[player.id] - course.parValues[currentHoleIndex];
                      if (relation > 0) return `(+${relation})`;
                      if (relation < 0) return `(${relation})`;
                      return '(E)';
                    })()}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-medium text-gray-400">
                    {course.parValues[currentHoleIndex]}
                  </div>
                  <div className="text-xs text-gray-400">Par</div>
                </div>
              )}
            </div>
            <button
              onClick={() => {
                const currentScore = currentScores[player.id] || course.parValues[currentHoleIndex];
                handleScoreChange(player.id, (currentScore + 1).toString());
              }}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
              type="button"
            >
              +
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    tournament.status === 'completed' ? (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Tournament Completed
            </h3>
            <p className="text-gray-600 max-w-sm mx-auto">
              This tournament has been completed. Please view the results on the leaderboard.
            </p>
            <button
              onClick={() => {
                if (window.location.pathname.includes('/scoring')) {
                  window.location.href = window.location.pathname.replace('/scoring', '/leaderboard');
                }
              }}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              View Leaderboard
            </button>
          </div>
        </div>
      </div>
    ) : (
      <div className="bg-white rounded-lg shadow-md p-6 safe-area-bottom">
        {renderScoreEntry()}
        
        <div className="space-y-3 mt-6">
          <button
            onClick={handleSubmitScores}
            disabled={false}
            className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <Save className="w-5 h-5 mr-2" />
            Save Scores
          </button>
          
          {!editingHole && areAllHolesCompleted() && (
            <button
              onClick={() => setShowConfirmation(true)}
              className="w-full bg-amber-600 text-white py-2 px-4 rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 flex items-center justify-center"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Complete Round
            </button>
          )}
        </div>

        {/* Confirmation Dialog */}
        {showConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="w-6 h-6 text-amber-500" />
                <h3 className="text-lg font-semibold">Verify Scores</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Please verify all players' scores before completing the round.
                You can review and edit scores if needed.
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={handleScoreReview}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Review Scores
                </button>
                <button
                  onClick={handleConfirmComplete}
                  className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700"
                >
                  Complete Round
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Score Review Modal */}
        {showReviewModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Review Scores</h3>
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                {selectedPlayers.map(player => {
                  // Calculate front nine total (OUT)
                  const frontNineTotal = [...Array(9)].reduce((sum, _, i) => {
                    const score = scores[player.id]?.[i + 1] || course.parValues[i];
                    return sum + score;
                  }, 0);

                  // Calculate back nine total (IN)
                  const backNineTotal = [...Array(9)].reduce((sum, _, i) => {
                    const score = scores[player.id]?.[i + 10] || course.parValues[i + 9];
                    return sum + score;
                  }, 0);

                  // Calculate total
                  const total = frontNineTotal + backNineTotal;

                  return (
                    <div key={player.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium">{player.name}</div>
                        <div className="flex gap-4 text-sm">
                          <div className="text-gray-600">
                            <span className="font-medium">OUT:</span> {frontNineTotal}
                          </div>
                          <div className="text-gray-600">
                            <span className="font-medium">IN:</span> {backNineTotal}
                          </div>
                          <div className="text-gray-900 font-medium">
                            <span className="font-medium">Total:</span> {total}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        {/* Front Nine */}
                        <div>
                          <div className="text-xs font-medium text-gray-500 mb-1">Front Nine</div>
                          <div className="grid grid-cols-9 gap-1">
                            {[...Array(9)].map((_, i) => {
                              const holeNumber = i + 1;
                              const score = scores[player.id]?.[holeNumber] || course.parValues[i];
                              const par = course.parValues[i];
                              return (
                                <div key={holeNumber} className="text-center">
                                  <div className="text-xs text-gray-500">{holeNumber}</div>
                                  <div className={`text-sm ${getScoreClass(score, par)}`}>{score}</div>
                                  <div className="text-xs text-gray-400">{par}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        {/* Back Nine */}
                        <div>
                          <div className="text-xs font-medium text-gray-500 mb-1">Back Nine</div>
                          <div className="grid grid-cols-9 gap-1">
                            {[...Array(9)].map((_, i) => {
                              const holeNumber = i + 10;
                              const score = scores[player.id]?.[holeNumber] || course.parValues[i + 9];
                              const par = course.parValues[i + 9];
                              return (
                                <div key={holeNumber} className="text-center">
                                  <div className="text-xs text-gray-500">{holeNumber}</div>
                                  <div className={`text-sm ${getScoreClass(score, par)}`}>{score}</div>
                                  <div className="text-xs text-gray-400">{par}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Back to Scoring
                </button>
                <button
                  onClick={handleReviewComplete}
                  className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700"
                >
                  Complete Round
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  );
}

export { GroupScoring }